/**
 * useIPCamera Hook
 *
 * Hook for capturing photos from IP cameras (RTSP, HTTP, ONVIF) via Electron IPC.
 * This hook ONLY works in the Electron environment. In browser, it returns
 * a disabled state.
 *
 * Features:
 * - RTSP snapshot capture using ffmpeg (via Electron main process)
 * - HTTP snapshot capture (direct HTTP request)
 * - Connection testing
 * - Automatic Electron detection
 */

import { useState, useCallback, useEffect } from 'react';
// Type declarations for window.electronAPI are in src/utils/platform.ts

// Types for camera configuration
export interface IPCameraConfig {
  protocol: 'rtsp' | 'http' | 'mjpeg';
  ipAddress: string;
  port: number;
  username?: string;
  password?: string;
  rtspPath?: string;
  snapshotPath?: string;
}

// Types for hook state
export interface IPCameraState {
  isElectron: boolean;
  ffmpegAvailable: boolean;
  ffmpegVersion?: string;
  isCapturing: boolean;
  isTesting: boolean;
  capturedImage: string | null;
  error: string | null;
}

// Types for hook return
export interface UseIPCameraReturn extends IPCameraState {
  captureSnapshot: (config: IPCameraConfig) => Promise<string | null>;
  testConnection: (config: IPCameraConfig) => Promise<{ success: boolean; message?: string; error?: string }>;
  clearCapturedImage: () => void;
  clearError: () => void;
  checkFfmpegAvailability: () => Promise<void>;
}

/**
 * Check if running in Electron environment
 * This is a synchronous check that can be called at any time
 */
function isElectronEnvironment(): boolean {
  const hasElectronAPI = typeof window !== 'undefined' && window.electronAPI !== undefined;
  return hasElectronAPI;
}

// Get initial electron state synchronously
const getInitialElectronState = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.electronAPI !== undefined;
};

/**
 * useIPCamera Hook
 *
 * @returns IPCamera state and functions
 */
export function useIPCamera(): UseIPCameraReturn {
  // Initialize with actual Electron detection - this runs synchronously on first render
  const [state, setState] = useState<IPCameraState>(() => ({
    isElectron: getInitialElectronState(),
    ffmpegAvailable: false,
    ffmpegVersion: undefined,
    isCapturing: false,
    isTesting: false,
    capturedImage: null,
    error: null,
  }));

  // Check Electron environment on mount
  useEffect(() => {
    const isElectron = isElectronEnvironment();
    setState((prev) => ({ ...prev, isElectron }));

    // If in Electron, check ffmpeg availability
    if (isElectron) {
      checkFfmpegAvailability();
    }
  }, []);

  /**
   * Check if ffmpeg is available
   */
  const checkFfmpegAvailability = useCallback(async () => {
    console.log('[useIPCamera] checkFfmpegAvailability called');
    if (!isElectronEnvironment()) {
      console.log('[useIPCamera] Not in Electron, skipping ffmpeg check');
      return;
    }

    try {
      console.log('[useIPCamera] Calling window.electronAPI.checkFfmpeg()...');
      const result = await window.electronAPI.checkFfmpeg();
      console.log('[useIPCamera] checkFfmpeg result:', result);
      setState((prev) => ({
        ...prev,
        ffmpegAvailable: result.available,
        ffmpegVersion: result.version,
        error: result.error || null,
      }));
    } catch (err) {
      console.error('[useIPCamera] Error checking ffmpeg:', err);
      setState((prev) => ({
        ...prev,
        ffmpegAvailable: false,
        error: 'Failed to check ffmpeg availability',
      }));
    }
  }, []);

  /**
   * Capture a snapshot from an IP camera
   * IMPORTANT: This function avoids setState during capture to prevent React re-renders
   * from interrupting the async operation
   */
  const captureSnapshot = useCallback(
    async (config: IPCameraConfig): Promise<string | null> => {
      console.log('[useIPCamera] captureSnapshot called with config:', config);

      if (!isElectronEnvironment()) {
        console.log('[useIPCamera] Not in Electron environment!');
        return null;
      }

      // For RTSP, check ffmpeg availability directly
      if (config.protocol === 'rtsp') {
        console.log('[useIPCamera] RTSP protocol - checking ffmpeg availability...');
        try {
          const ffmpegCheck = await window.electronAPI.checkFfmpeg();
          console.log('[useIPCamera] ffmpeg check result:', ffmpegCheck);
          if (!ffmpegCheck.available) {
            console.log('[useIPCamera] ffmpeg not available');
            return null;
          }
          console.log('[useIPCamera] ffmpeg is available, proceeding with capture...');
        } catch (err) {
          console.error('[useIPCamera] Error checking ffmpeg:', err);
          return null;
        }
      }

      // NO setState here - just proceed directly to capture
      try {
        console.log('[useIPCamera] Calling captureIPCameraSnapshot...');
        const result = await window.electronAPI.captureIPCameraSnapshot({
          protocol: config.protocol,
          ipAddress: config.ipAddress,
          port: config.port,
          username: config.username,
          password: config.password,
          rtspPath: config.rtspPath,
          snapshotPath: config.snapshotPath,
        });
        console.log('[useIPCamera] captureIPCameraSnapshot result:', result.success, result.error);

        if (result.success && result.data) {
          // Only update state AFTER we have the result
          setState((prev) => ({
            ...prev,
            ffmpegAvailable: true,
            capturedImage: result.data!,
            error: null,
          }));
          return result.data;
        } else {
          console.log('[useIPCamera] Capture failed:', result.error);
          return null;
        }
      } catch (err) {
        console.error('[useIPCamera] Error capturing snapshot:', err);
        return null;
      }
    },
    []
  );

  /**
   * Test connection to an IP camera
   */
  const testConnection = useCallback(
    async (
      config: IPCameraConfig
    ): Promise<{ success: boolean; message?: string; error?: string }> => {
      if (!isElectronEnvironment()) {
        return {
          success: false,
          error: 'IP Camera test is only available in the Electron desktop app',
        };
      }

      setState((prev) => ({
        ...prev,
        isTesting: true,
        error: null,
      }));

      try {
        const result = await window.electronAPI.testIPCameraConnection({
          protocol: config.protocol,
          ipAddress: config.ipAddress,
          port: config.port,
          username: config.username,
          password: config.password,
        });

        setState((prev) => ({
          ...prev,
          isTesting: false,
          error: result.error || null,
        }));

        return result;
      } catch (err) {
        console.error('[useIPCamera] Error testing connection:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error testing connection';
        setState((prev) => ({
          ...prev,
          isTesting: false,
          error: errorMessage,
        }));
        return { success: false, error: errorMessage };
      }
    },
    []
  );

  /**
   * Clear captured image
   */
  const clearCapturedImage = useCallback(() => {
    setState((prev) => ({ ...prev, capturedImage: null }));
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    captureSnapshot,
    testConnection,
    clearCapturedImage,
    clearError,
    checkFfmpegAvailability,
  };
}

export default useIPCamera;
